import { useState } from 'react';
import { Link } from 'react-router-dom';
import FusionIcon from '../../../components/icons/FusionIcon.js';
import PlanningIcon from '../../../components/icons/PlanningIcon.js';
import WorkfrontIcon from '../../../components/icons/WorkfrontIcon.js';
import { Checkbox } from '../../../components/ui/checkbox.js';
import { useDemoMode } from '../../demo/index.js';

/**
 * Three rendering surfaces share the same `<HomeContentCard/>` hero:
 *
 *   1. `<LandingPage/>` (this file) — chromeless full-viewport
 *      welcome rendered at `/`. The very first thing the operator
 *      sees on a fresh visit. No sidebar / no top bar — just the
 *      hero popup centered over the bokeh background.
 *   2. `<HomePage/>` (this file) — same hero rendered **inside**
 *      `<IframeShell>` at `/home`. The sidebar Home menu item points
 *      here so picking Home from any slice route gives you the same
 *      view with the left rail still visible. Operator-reported:
 *      "when clicking on home menu item, user should see home page
 *      with left menu".
 *   3. `<HomePopupOverlay/>` (in `IframeShell.tsx`) — the same hero
 *      rendered as a dimmed-backdrop overlay on top of the current
 *      page. Triggered by the sidebar WorkFocus logo click; no
 *      navigation. Operator-reported: "when user click on logo -
 *      home page content should show as a popup without switching
 *      a page".
 *
 * `HomeContentCard` is exported so all three call sites paint
 * identical hero markup.
 */
export function LandingPage(): JSX.Element {
  return (
    <>
      {/* Bokeh background fills the whole viewport in dark mode.
          Light-mode falls back to the cream `--bg-page` token from
          the ThemeProvider so the landing surface stays clean even
          when no chrome is drawn around it. */}
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-0 -z-10 hidden bg-cover bg-center bg-no-repeat dark:block"
        style={{ backgroundImage: "url('/img/bg_about_page.png')" }}
      />
      <div
        aria-hidden="true"
        className="fixed inset-0 -z-20 dark:hidden"
        style={{ background: 'var(--bg-page, var(--gradient-end))' }}
      />

      <div className="relative flex min-h-screen items-center justify-center p-4 sm:p-6">
        <HomeContentCard variant="page" />
      </div>
    </>
  );
}

/**
 * Chromed Home page — same hero card, but rendered inside the
 * `IframeShell`. Reachable via the sidebar's Home menu item
 * (`/home`).
 */
export function HomePage(): JSX.Element {
  return (
    <div className="relative flex min-h-[calc(100vh-7rem)] items-center justify-center p-4 sm:p-6">
      <HomeContentCard variant="page" />
    </div>
  );
}

interface HomeContentCardProps {
  /** Closes the parent popup after a product card is clicked. Only
   *  meaningful when `variant="popup"` — popup navigation should
   *  also dismiss the overlay so the operator lands on the picked
   *  route without manually closing first. */
  onPick?: () => void;
  /** Visual variant. `popup` removes the page-level outer padding
   *  (the Dialog overlay provides its own) and lets the dialog drive
   *  width / max-width. */
  variant?: 'page' | 'popup';
}

/**
 * Inner hero card — logo + 3 product tiles + demo toggle + footer
 * external link row. Used by both the chromed Home page and the
 * logo-triggered popup overlay (see `HomePopup`). All routing and
 * demo-mode state lives here so the two call sites share behaviour
 * verbatim.
 */
export function HomeContentCard({ onPick, variant = 'page' }: HomeContentCardProps): JSX.Element {
  const [saveChoice, setSaveChoice] = useState(false);
  const { isDemoMode, toggleDemoMode } = useDemoMode();

  // Customer-visible SPA generation label. Bumped from v2.0 (legacy)
  // to v3.0 to mark the rewrite. Operator-requested: "lets also
  // change version to Version: 3.0 (v3.0.1)". The fixed v3.0.1
  // string is independent of the engine's commit-sha-driven version.
  const versionLabel = 'Version: 3.0 (v3.0.1)';

  return (
    <div
      className={`relative flex w-full max-w-[936px] flex-col overflow-hidden rounded-3xl border ${
        variant === 'popup' ? '' : ''
      }`}
      style={{
        borderColor: 'var(--brand-blue)',
        background: 'var(--gradient-end)',
        minHeight: variant === 'popup' ? undefined : 'min(634px, calc(100vh - 8rem))',
      }}
    >
      {/* Demo-mode checkbox, pinned to the top-left corner of the
          popup. Demo mode persists across navigation via
          `DemoModeProvider`'s localStorage backing. Uses the shared
          `Checkbox` Radix primitive with the legacy About-popup
          colour treatment (white box, black tick) so it matches the
          rest of the popup chrome. Label shortened to just "Demo"
          per operator note. */}
      <label
        htmlFor="cbDemoMode"
        className="absolute left-3 top-3 z-10 flex cursor-pointer items-center gap-2 rounded-md border border-white/20 bg-black/30 px-2 py-1 text-xs text-white backdrop-blur-sm transition-colors hover:bg-black/40 sm:left-4 sm:top-4"
      >
        <Checkbox
          id="cbDemoMode"
          checked={isDemoMode}
          onCheckedChange={() => toggleDemoMode()}
          className="h-3.5 w-3.5 rounded border-white bg-white data-[state=checked]:bg-white data-[state=checked]:text-black"
        />
        <span className="font-medium">Demo</span>
      </label>

      {/* Inner gradient card */}
      <div
        className="mx-auto flex w-full flex-col rounded-3xl border-b p-3 sm:p-4 md:p-6"
        style={{
          borderBottomColor: 'var(--brand-blue)',
          background: 'linear-gradient(180deg, var(--gradient-end) 0%, var(--gradient-start) 100%)',
        }}
      >
        {/* Logo + version. Text is forced white (not theme-aware
            `text-foreground`) because the popup's background is the
            brand dark gradient regardless of the active theme —
            operator-reported: "do not change text color for home page
            content, because of static dark background, text should
            stay as in dark mode". */}
        <div className="flex flex-col items-center">
          <img
            src="/logo_about_page.svg"
            alt="WorkFocus Logo"
            width={290}
            height={115}
            className="h-auto w-48 sm:w-60 md:w-[290px]"
          />
          <p className="mt-1 text-base text-white sm:text-[18px]">Center of Excellence</p>
          <p className="text-[10px] text-white sm:text-[12px]">{versionLabel}</p>
        </div>

        {/* Three product tiles */}
        <div className="mt-4 grid grid-cols-1 gap-3 sm:mt-6 sm:grid-cols-2 sm:gap-4 md:grid-cols-3">
          <AboutStartItem
            to="/scenarios/developer"
            title="Fusion"
            subtitle="Leverage our deep expertise in Adobe Workfront Fusion to help you automate Workfront workflows and integrate enterprise applications."
            icon={<FusionIcon className="mx-auto my-4 h-[64px] w-[64px]" />}
            onPick={onPick}
          />
          <AboutStartItem
            to="/workfront"
            title="Workfront"
            subtitle="Designed to unlock the full potential on your work management ecosystem through organizational efficiency and seamless integration."
            icon={<WorkfrontIcon className="mx-auto my-4 h-[64px] w-[64px]" />}
            onPick={onPick}
          />
          <div className="sm:col-span-2 md:col-span-1">
            <AboutStartItem
              to="/planning/er-diagram"
              title="Planning"
              subtitle="Integrate AEM Assets that let's you easily discover, govern, and activate millions of assets for your Content Supply Chain."
              icon={<PlanningIcon className="mx-auto my-4 h-[64px] w-[64px]" />}
              onPick={onPick}
            />
          </div>
        </div>

        {/* "Make my choice a default view" — uses the shared Radix
            Checkbox primitive with the legacy About-popup className:
            white box, black tick. Verbatim port of
            `wf-audit/.../about/Provider.tsx:160` so the legacy + new
            popups look identical for tenants doing side-by-side
            comparison during cutover. */}
        <div className="mt-4 flex items-center gap-2 sm:mt-6 sm:gap-3">
          <Checkbox
            id="cbSaveChoice"
            checked={saveChoice}
            onCheckedChange={(checked) => setSaveChoice(checked === true)}
            className="rounded-md border-white bg-white data-[state=checked]:bg-white data-[state=checked]:text-black"
          />
          <label
            htmlFor="cbSaveChoice"
            className="cursor-pointer text-sm font-normal text-white sm:text-base"
          >
            Make my choice a default view
          </label>
        </div>
      </div>

      {/* Footer link row — port of legacy `AboutLinkButton`:
          gold text, gold external-link arrow, fixed-height pills. */}
      <div className="mx-3 mb-3 mt-4 flex flex-col items-stretch gap-3 sm:mx-4 sm:mb-4 sm:mt-6 sm:gap-4 md:mx-6 md:mb-6 md:mt-0 md:flex-row md:items-center md:justify-between md:gap-6">
        <AboutLinkButton
          title="Documentation"
          icon={<DocIcon />}
          href="https://workfocus.io/fcoe/docs/introduction"
        />
        <AboutLinkButton
          title="Get Support"
          icon={<HeadsetIcon />}
          href="https://workfocus.io/fcoe/support"
        />
        <AboutLinkButton title="workfocus.io" icon={<GlobeIcon />} href="https://workfocus.io" />
      </div>
    </div>
  );
}

/* ── AboutStartItem — port of
   `wf-audit/src/slices/common/components/about/AboutStartItem.tsx`.
   Uses the `.container-animation` class for the gradient-sweep hover. */
function AboutStartItem({
  to,
  title,
  subtitle,
  icon,
  onPick,
}: {
  to: string;
  title: string;
  subtitle: string;
  icon: JSX.Element;
  onPick?: () => void;
}): JSX.Element {
  return (
    <Link
      to={to}
      onClick={onPick}
      className="container-animation block h-[252px] w-full rounded-xl transition-transform duration-200 ease-in-out hover:scale-[1.04]"
    >
      <div
        className="content flex h-full cursor-pointer flex-col rounded-xl border border-white/10 px-2 py-2 text-center text-white"
        style={{
          background: 'linear-gradient(180deg, var(--gradient-start) 0%, var(--gradient-end) 100%)',
        }}
      >
        {icon}
        <div className="pb-0 text-[22px] leading-normal" style={{ color: 'var(--brand-yellow)' }}>
          {title}
        </div>
        <div className="whitespace-normal break-words text-center text-sm font-normal leading-[140%] text-gray-100">
          {subtitle}
        </div>
      </div>
    </Link>
  );
}

/* ── AboutLinkButton — port of legacy markup.
   Gold text (`text-yellowWf`), gold external-link icon, h-12 pill. */
function AboutLinkButton({
  title,
  icon,
  href,
}: {
  title: string;
  icon: JSX.Element;
  href: string;
}): JSX.Element {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="container-animation flex h-12 w-full flex-col items-center justify-center rounded-xl text-base font-medium leading-[120%] transition-transform duration-200 ease-in-out hover:scale-[1.04] sm:text-lg md:w-[280px] md:text-[20px]"
      style={{
        color: 'var(--brand-yellow)',
        background: 'linear-gradient(180deg, var(--gradient-start) 0%, var(--gradient-end) 100%)',
      }}
    >
      <div
        className="content flex w-full items-center gap-2 rounded-xl border border-white/10 px-2 font-normal"
        style={{
          background: 'linear-gradient(180deg, var(--gradient-start) 0%, var(--gradient-end) 100%)',
        }}
      >
        <span className="shrink-0">{icon}</span>
        <span className="flex-1 truncate pl-2">{title}</span>
        <ExternalLinkGlyph />
      </div>
    </a>
  );
}

function DocIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M3 2H10L13 5V14H3V2Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path d="M10 2V5H13" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M5 8H11M5 10.5H11M5 13H9" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}
function HeadsetIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path
        d="M2 10V8C2 4.5 4.5 2 8 2C11.5 2 14 4.5 14 8V10"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <rect x="1" y="9" width="3" height="5" rx="1" stroke="currentColor" strokeWidth="1.5" />
      <rect x="12" y="9" width="3" height="5" rx="1" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}
function GlobeIcon(): JSX.Element {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="6" stroke="currentColor" strokeWidth="1.5" />
      <path
        d="M2 8H14M8 2C10 4 10 12 8 14M8 2C6 4 6 12 8 14"
        stroke="currentColor"
        strokeWidth="1.5"
      />
    </svg>
  );
}
function ExternalLinkGlyph(): JSX.Element {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 13 13"
      fill="none"
      className="shrink-0"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5.75 0.5H2.75C1.50736 0.5 0.5 1.50736 0.5 2.75V10.25C0.5 11.4926 1.50736 12.5 2.75 12.5H10.25C11.4926 12.5 12.5 11.4926 12.5 10.25V7.25M8.75 0.5H12.5M12.5 0.5V3.875M12.5 0.5L6.124 6.875"
        stroke="var(--brand-yellow)"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
